import { Link } from "react-router-dom";

const SECTIONS = [
  {
    title: "Derivatives — Equity Options",
    items: [
      "Black-Scholes-Merton pricing with continuous dividend yield",
      "Full Greeks: delta, gamma, theta, vega, rho",
      "Newton-Raphson implied-volatility solver (64-iteration, 1e-6 tol)",
      "Delta-based margin: long options capped at premium paid, short options with REG-T 50% floor",
      "Margin engine falls back to conservative gross-notional when IV is unavailable",
      "ORM fields per order: strike, expiry date, option_right (CALL/PUT)",
      "Settlement engine: CALL/PUT cash settlement at expiry — intrinsic value vs strike, OTM expires worthless",
      "American-exercise / dividend-discrete pricing ready to swap to py_vollib (stable surface)",
    ],
  },
  {
    title: "Derivatives — CFDs",
    items: [
      "ESMA retail leverage caps by sub-class: major FX 30×, minor FX/gold 20×, commodity 10×, index/stock-CFD 5×, crypto 2×",
      "Per-instrument override and per-account-group unlock via appropriateness test",
      "Initial margin: notional ÷ leverage, computed pre-trade",
      "Contract size and pip value stored in instrument metadata",
      "Daily overnight financing charge (long bps / short bps) via SwapRunner daemon",
      "SwapRunner runs at NY rollover 17:00 ET with DST awareness",
      "Idempotent swap: unique-by-(account, symbol, day) prevents double-charging on restarts",
    ],
  },
  {
    title: "Derivatives — Futures, Forwards & NDFs",
    items: [
      "AssetClass enum: CFD, CRYPTO_PERP, EQUITY_OPTION, FUTURE, FX_FORWARD, NDF, COMMODITY, BOND",
      "contract_month field (e.g. 'Mar25') for futures series identification",
      "value_date field on orders for forward and NDF settlement date",
      "Settlement daemon (hourly, leader-locked): closes FUTURE positions at settlement_price from instrument metadata",
      "FX_FORWARD / NDF settlement: fixing rate vs contracted rate × notional, credited as AccountLedger 'settlement' entries",
      "Idempotent settlement: settled_at written to instrument metadata, re-run is no-op",
      "FX triangulation engine via USD/EUR bridge (direct, inverse, one-hop synthetic)",
      "Wednesday triple swap — 3× NY-rollover multiplier on Wednesday (FX T+2 weekend bridge), 1× every other day; ledger note tags [3x wednesday]",
    ],
  },
  {
    title: "Derivatives — Crypto Perpetuals & Bonds",
    items: [
      "Funding rate daemon (15-min sweep, leader-locked): applies per-symbol funding rate to all open perp positions",
      "Funding direction: long pays when rate > 0, receives when rate < 0; short reverses",
      "Watermark-based idempotency — replay of funding is a no-op",
      "Crypto perpetual funding rates stored in funding_rates table per symbol",
      "Bond pricing: accrued interest with 30/360, ACT/365, ACT/ACT day-count modes",
      "Dirty price = clean price + accrued interest",
      "Yield-to-maturity: Newton-Raphson solver with current-yield fallback on non-convergence",
    ],
  },
  {
    title: "Trading Engine",
    items: [
      "Market, Limit, Stop, Stop-Limit, OCO, Bracket, AON, Min-Qty orders",
      "Institutional algos: VWAP, TWAP, Iceberg child-slice execution",
      "9 asset classes: Forex, CFD, Crypto, Equity Options, Futures, Forwards, NDFs, Commodity, Bond",
      "Order idempotency: client_order_id token, DB-level UNIQUE(org, client_order_id), replay returns original fill",
      "Internal limit order book (L2, rehydrated from DB on startup)",
      "A-book / B-book / mixed internalization per account group",
      "Multi-LP concurrent routing with connection pooling",
      "Best-execution planning for A-book legs",
      "BBO quote aggregation and client markup engine",
      "Dynamic spread widening on news events",
      "LP adapters: FIX 4.4, REST/WS (Binance), Alpaca (US equities — REST + IEX WebSocket quotes), OANDA (FX — streaming prices + v20 orders), mock adapter for testing",
      "Drop-copy FIX out: DropCopyStarter boots one outbound FIX 4.4 session per DROP_COPY LP row — real socket, ExecutionReport on every fill",
      "Fill reconciliation: LP fills → orders → positions",
    ],
  },
  {
    title: "Risk Management",
    items: [
      "Real-time risk engine evaluating every event",
      "Configurable per-account risk limits vs equity",
      "Circuit breakers — halt trading on breach",
      "Real-time margin calculator with negative balance protection",
      "Cross-asset correlation tracker",
      "Automated position sizing (fixed fractional, Kelly, vol-targeting via robots)",
      "Hedge mode and firm exposure netting",
      "Pre-trade checks before every order submission",
      "Risk snapshot broadcast via WebSocket",
    ],
  },
  {
    title: "Order & Position Management",
    items: [
      "Full order lifecycle state machine with broker ID mapping",
      "Parent/child order tracking for bracket and OCO pairs",
      "TIF enforcement: GTC, GTD, FOK, IOC, Day — daemon-driven expiry sweeps",
      "Real-time position P&L from fills",
      "Partial position close",
      "Cancel all orders / close all positions in one action",
      "Layered stop-loss strategies with ATR and support/resistance helpers",
      "Trailing stops and take-profit merge logic",
      "FIFO / LIFO / SpecID / HIFO lot-level accounting",
    ],
  },
  {
    title: "Strategies & Automation",
    items: [
      "Strategy framework: plugin registry, lifecycle manager, DB snapshots",
      "Built-in: Trend Follower, Breakout, Grid, Mean Reversion, Scalper, Multi-Signal",
      "Built-in: Statistical Arbitrage, Triangular FX Arbitrage",
      "Robot (Expert Advisor) system — multi-tenant, persisted, crash-resilient",
      "Robot journal — structured trade logging to DB",
      "Cross-venue arbitrage detector and price aggregator",
      "Full backtesting engine: bar replay, simulated broker, equity curve",
      "Arb-specific backtest simulator with multi-exchange tick replay",
      "CLI: run live/paper strategy, run backtest, print report",
      "Strategy tester & optimizer in native desktop terminal",
      "MT4 bridge adapter · MT5 bridge adapter",
      "Copy trading: leader fills replicated to followers with quantity scaling and drawdown-based stop",
    ],
  },
  {
    title: "ML Insights",
    items: [
      "Directional signal model: 5-feature logistic regression over RSI, MACD histogram, Bollinger position, ADX, volume z-score",
      "Outputs score 0–100 with bullish / neutral / bearish label — honest 60% accuracy on v1",
      "Model weights in JSON (no scikit-learn required at runtime — plain math)",
      "Anomaly detector: EWMA rolling z-score over close-to-close returns — warn 3σ, alert 5σ, critical 8σ",
      "Flash-crash / stuck-feed / out-of-band LP quote detection",
      "Symbol recommender: ranks universe by signal × inverse-volatility (stable mid-confidence beats turbulent high-confidence); excludes currently-held positions (no double-recommending); optional asset-class filter",
      "Recommender v2: `held_symbols` parameter excludes open positions; `asset_class_filter` surfaces only the requested asset class",
      "GET /api/v1/ml/signals/{symbol} — per-symbol score, cached 60s",
      "GET /api/v1/ml/anomalies/{symbol} — incremental EWMA anomaly detection (Redis-backed per-symbol state; updates from one return vs. full history rebuild; falls back to stateless path when Redis unavailable)",
      "GET /api/v1/ml/recommended — top-N ranked symbols by direction",
      "Offline trainer (python -m trader.ml.train) — synthesizes 60 regime-mixing series, fits via gradient descent",
      "Insights page in web terminal: Recommended + Signals + Anomalies panels",
    ],
  },
  {
    title: "CRM Integrations",
    items: [
      "HubSpot adapter: per-event lifecyclestage update, custom piggy_org_id / piggy_user_id props",
      "Salesforce adapter: OAuth client-credentials, POST to /sobjects/Lead with per-event Status mapping",
      "Zoho CRM adapter: OAuth refresh-token flow, multi-region (US/EU/IN/AU/JP/CA)",
      "Events tracked: signup, kyc_pass, kyc_fail, first_deposit, first_trade, dormant_30d, churn",
      "Idempotent dispatch: SELECT-before-INSERT on (org_id, vendor, external_event_id)",
      "First-trade hook wired into fill pipeline — fires automatically on account's first fill",
      "Vendor config per org via org.settings.crm — UnknownVendor silently no-ops",
    ],
  },
  {
    title: "Technical Indicators (21 built-in)",
    items: [
      "Moving averages: SMA, EMA, WMA, DEMA, TEMA, HMA",
      "Oscillators: RSI, MACD, Stochastic, CCI, Williams %R",
      "Volatility: ATR, Bollinger Bands, Keltner Channel",
      "Trend: ADX, Parabolic SAR, Supertrend, Ichimoku Cloud",
      "Volume: VWAP",
      "Channels: Donchian Channel",
      "Pivots: Classic pivot points",
    ],
  },
  {
    title: "Market Data",
    items: [
      "Live multi-exchange tick fan-in via CCXT provider",
      "CSV historical data provider for backtesting",
      "Multi-provider feed with per-symbol source routing",
      "Data replay orchestration with backtest clock",
      "Real-time bar aggregation and cache",
      "News feed and economic calendar",
    ],
  },
  {
    title: "Finance & Treasury",
    items: [
      "Double-entry general ledger — chart of accounts, journals, trial balance",
      "Client deposit flows: Stripe, Plaid, Fireblocks, mock processors",
      "Segregated client funds with bank reconciliation runs",
      "NAV snapshots with pledged-asset tracking",
      "IB commission tracking, reserve accounts, referral accounting",
      "Corporate actions: stock splits, dividends, spin-offs, mergers (lot transfer + cash component), spin-offs (parent intact, new spin lot)",
      "Inactivity fee handling",
      "Promo codes and redemptions",
      "Configurable fee schedule (per instrument, per account group)",
      "Tax engine: realized P&L, wash-sale detection, 1099-B, Form 8949, MIFID II statement",
      "Cost-basis lot tracking: FIFO, LIFO, SpecID, HIFO",
      "Withdrawals with admin approval flow",
      "Bond pricing: accrued interest (30/360, ACT/365, ACT/ACT), dirty price, YTM solver",
      "Settlement engine: futures, options, FX forwards, NDFs — hourly daemon, idempotent",
      "Funding rate engine: perpetual crypto funding applied every 15 min, watermark-based",
    ],
  },
  {
    title: "Compliance & Regulatory",
    items: [
      "KYC identity verification — Bloomberg ARM adapter + Sumsub (HMAC-SHA256 signed) real integrations",
      "KYT (Know Your Transaction) AML monitoring: structuring, velocity, round-amount, high-risk-jurisdiction, dormant-then-large rules",
      "OFAC, PEP and global sanctions screening with 6-hour rescan daemon (leader-locked)",
      "Audit log append-only: Postgres trigger prevents UPDATE/DELETE — even raw DB credentials can't rewrite history",
      "MIFID II Art. 25(3) appropriateness / suitability test",
      "RTS 22 transaction reporting → ARM adapter (ESMA XML)",
      "RTS 27 / RTS 28 best-execution report builder (including rejected LP order percentage per venue)",
      "Trade surveillance: wash trade, layering, spoofing, marking-the-close, front-running detectors",
      "FCA / CySEC complaint SLA workflow with clock",
      "Cooling-off period enforcement for risk-affecting changes",
      "Negative balance protection (explicit rule engine)",
      "GDPR: data retention (robot_journal 1yr, bars 2yr, usage_events 7yr, push 90d), right-to-erasure",
      "Disaster recovery runbooks (SOC2, DR, regions)",
    ],
  },
  {
    title: "Analytics & Reporting",
    items: [
      "Portfolio P&L attribution and drawdown analysis",
      "Equity curve, returns analytics, tearsheet generation",
      "Transaction Cost Analysis (TCA): slippage and latency by venue, symbol and hour-of-day",
      "Admin Business Intelligence: AUM, volume, revenue, LP scorecard, cohort retention, LTV",
      "Tax document generation: 1099-B, 8949, MIFID II summary",
      "Execution quality snapshots per fill (best-exec)",
      "Account statements (PDF / CSV)",
    ],
  },
  {
    title: "Auth & Identity",
    items: [
      "JWT bearer authentication with refresh tokens",
      "HttpOnly cookie auth (piggy_session): HttpOnly + Secure + SameSite=Lax — XSS cannot exfiltrate the token",
      "CSRF Origin-check middleware — validates Origin/Referer for all cookie-auth state-changing requests",
      "nginx / Caddy security headers applied (live)",
      "TOTP / FIDO2 multi-factor authentication",
      "MFA recovery codes: 10 single-use backup codes minted on enrolment, sha256-hashed, regeneratable",
      "OIDC SSO integration (any provider)",
      "Fine-grained RBAC with role/permission union",
      "Super-admin bypass for platform operators",
      "Redis-backed rate limiting: orders 60/min, flatten 5/min, withdrawals 10/min",
      "Impersonation audit middleware",
      "Correlation ID middleware: X-Request-Id bound to every request in structured logs, echoed in response",
      "API key management with scoped permissions",
    ],
  },
  {
    title: "Multi-tenant Platform",
    items: [
      "Organization / tenant isolation with row-level security",
      "Per-tenant custom branding (logo, colors, name)",
      "Custom domain per tenant (Caddy integration)",
      "Org-level feature flags",
      "White-label OIDC per org",
      "Account groups with A/B-book routing rules",
      "Dealing desk override flows",
      "Usage metering and billing events",
    ],
  },
  {
    title: "Notifications & Integrations",
    items: [
      "In-app notification center with real-time WebSocket delivery",
      "Email: SMTP and console backends (verify, forgot-password, margin call)",
      "Web push (VAPID), APNS (iOS) and FCM (Android) push notifications",
      "Outbound webhooks with retry delivery and delivery log",
      "Marketing campaign engine with subscription management",
      "Price and margin alert rules",
    ],
  },
  {
    title: "Infrastructure & Observability",
    items: [
      "FastAPI + async SQLAlchemy, Postgres 16, Redis 7",
      "Alembic migrations with downgrade test coverage — every migration round-tripped",
      "Prometheus metrics: HTTP latency histograms, order/fill counters, WS connection gauge, daemon sweep/error counters",
      "GET /piggy/metrics — standard Prometheus scrape endpoint",
      "Published SLA: p50 ~300ms, p99 ~385ms (full regulatory stack); CI fails on regression",
      "APM via Sentry and/or OpenTelemetry",
      "Bounded LRU caches: configurable max_size, atomic replace, LRU eviction",
      "WebSocket per-connection backpressure: asyncio.Queue per slot, drops oldest frame on overflow",
      "CORS: config-driven allow_origins, auto-downgrades credentials on wildcard",
      "Status page (public-facing, API-driven)",
      "16 background daemons — all leader-locked for HA multi-replica deployments",
      "Health endpoint: real DB + Redis ping with 2s timeout, 503 on failure",
      "Race-safe balance updates: FOR UPDATE across all 7 trading_account.balance sites",
      "All data endpoints scoped to the calling user — no cross-tenant or cross-user leakage",
      "Demo account self-serve: $100,000 paper seed, 30-day TTL",
      "694+ backend tests · UI test framework: vitest + @testing-library/react",
    ],
  },
];

const WEB_FEATURES = [
  "Full trading terminal — configurable multi-pane layout",
  "Live chart pane, order book (L2), tape, margin pane, equity curve pane",
  "One-click order ticket with SL/TP, TIF and fill-policy controls",
  "Positions pane with real-time P&L and partial-close",
  "Open orders and order history",
  "Watchlist with live bid/ask",
  "Portfolio page — holdings, attribution, drawdown",
  "Analytics — returns, equity curve, commission breakdown",
  "Statements — downloadable account statements",
  "Strategies page — run, pause, resume live strategies",
  "Robots page — manage Expert Advisors",
  "Tearsheet — institutional performance report with robot/strategy filter",
  "ML Insights page — directional signals, anomaly alerts, symbol recommendations",
  "Copy trading — follow leaders, track replicated fills",
  "Academy — embedded learning module",
  "Discover — explore instruments",
  "Alerts — price and condition alerts",
  "Profile, API keys, trading accounts, broker accounts",
  "Withdrawals with status tracking",
  "Appropriateness / suitability questionnaire flow",
  "Complaints submission and status",
  "Command palette (keyboard-driven navigation)",
  "Real-time notification center",
  "Onboarding wizard for new accounts",
  "Org switcher for multi-tenant users",
      "Full admin panel: users, KYC, AML, sanctions, surveillance, best-exec, RTS22, BI, account groups, LP sessions (FIX/REST/Alpaca/OANDA), risk limits, orgs, audit log, usage, branding, custom domains (with ACME cert webhook for automated ssl_status → active), OIDC, complaints, withdrawals, dealing desk",
  "Dark mode, i18n (EN / ES)",
];

const NATIVE_FEATURES = [
  "GPU-accelerated candlestick, bar and line charts",
  "Multi-chart workspace — single, tile and cascade layouts",
  "Chart indicators overlay (MA, RSI, ATR, BB and more)",
  "Trendlines, rectangles and chart drawing objects",
  "Watchlist / Market Watch with configurable columns",
  "Depth of Market (L2 bid/ask ladder)",
  "Order ticket — market, limit, stop, bracket, trailing stop",
  "Quick buy/sell one-click from chart or DOM",
  "Modify and cancel limit orders in-pane",
  "Positions pane with partial close",
  "Open orders and full order history",
  "Alerts pane with sound/visual triggers",
  "Equity curve pane",
  "Navigator — instrument and account browser",
  "Toolbox / Journal — structured trade log",
  "News feed and economic calendar",
  "Internal mailbox",
  "Expert Advisor attach/detach per chart",
  "Auto-trading on/off master switch",
  "Strategy runtime logs",
  "Strategy tester and optimizer",
  "Strategy replay frame scrubbing",
  "Workspace save/restore and default template",
  "Full menu bar: File, View, Insert, Charts, Tools, Window",
  "Cross-platform: macOS (.dmg), Windows (NSIS installer), Linux (.tar.gz)",
  "Rust native core (cdylib) for tick ingestion and order validation",
  "Connects to Piggy backend via WebSocket + REST",
];

const STATS = [
  { v: "9", l: "Asset classes" },
  { v: "13+", l: "Order types" },
  { v: "8", l: "Built-in strategies" },
  { v: "21", l: "Indicators" },
  { v: "3", l: "Client surfaces" },
  { v: "3+", l: "Regulatory regimes" },
  { v: "16", l: "Background daemons" },
  { v: "~300ms", l: "p50 order latency" },
];

export function FeaturesPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Hero */}
      <div className="border-b border-slate-800 px-6 py-16 text-center">
        <p className="mb-3 text-xs uppercase tracking-widest text-slate-500">
          Piggy Trading Platform
        </p>
        <h1 className="mb-4 text-4xl font-semibold tracking-tight">
          A complete trading infrastructure,
          <br className="hidden sm:block" /> built from first principles.
        </h1>
        <p className="mx-auto mb-8 max-w-xl text-slate-400">
          Multi-tenant white-label platform covering execution, risk, compliance,
          analytics and automation — web terminal, native desktop app and mobile.
        </p>
        <Link
          to="/login"
          className="inline-block rounded bg-slate-100 px-5 py-2 text-sm font-medium text-slate-900 hover:bg-white"
        >
          Sign in
        </Link>
      </div>

      {/* Stats bar */}
      <div className="border-b border-slate-800">
        <div className="mx-auto grid max-w-5xl grid-cols-4 divide-x divide-slate-800 sm:grid-cols-8">
          {STATS.map(({ v, l }) => (
            <div key={l} className="px-4 py-6 text-center">
              <p className="text-2xl font-semibold">{v}</p>
              <p className="mt-0.5 text-xs text-slate-500">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Backend feature grid */}
      <div className="mx-auto max-w-5xl px-6 py-14">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Backend platform</p>
        <h2 className="mb-10 text-xl font-semibold">Engine, finance &amp; compliance</h2>
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {SECTIONS.map((sec) => (
            <div key={sec.title}>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                {sec.title}
              </p>
              <ul className="space-y-1.5">
                {sec.items.map((item) => (
                  <li key={item} className="flex gap-2 text-sm text-slate-300">
                    <span className="mt-0.5 shrink-0 text-slate-700">–</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-slate-800" />

      {/* Web terminal */}
      <div className="mx-auto max-w-5xl px-6 py-14">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Client surface 1</p>
        <h2 className="mb-2 text-xl font-semibold">Web Terminal</h2>
        <p className="mb-8 text-sm text-slate-400">
          React 18 + TypeScript + Tailwind. Full trading terminal in any browser — no install required.
        </p>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {WEB_FEATURES.map((f) => (
            <div key={f} className="flex gap-2 text-sm text-slate-300">
              <span className="mt-0.5 shrink-0 text-slate-700">–</span>
              {f}
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-slate-800" />

      {/* Native desktop */}
      <div className="mx-auto max-w-5xl px-6 py-14">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Client surface 2</p>
        <h2 className="mb-2 text-xl font-semibold">Native Desktop Terminal</h2>
        <p className="mb-4 text-sm text-slate-400">
          Qt 6 + C++20 + Rust core. Runs natively on macOS, Windows and Linux.
          GPU-accelerated charts and full order management — no browser tab required.
        </p>
        <div className="mb-8 flex flex-wrap gap-2">
          {[
            { label: "macOS .dmg", href: "/downloads/native/macos" },
            { label: "Windows .exe", href: "/downloads/native/windows" },
            { label: "Linux .tar.gz", href: "/downloads/native/linux" },
          ].map(({ label, href }) => (
            <a
              key={label}
              href={href}
              className="rounded border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              {label} ↓
            </a>
          ))}
        </div>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {NATIVE_FEATURES.map((f) => (
            <div key={f} className="flex gap-2 text-sm text-slate-300">
              <span className="mt-0.5 shrink-0 text-slate-700">–</span>
              {f}
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-slate-800" />

      {/* Mobile */}
      <div className="mx-auto max-w-5xl px-6 py-14">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Client surface 3</p>
        <h2 className="mb-2 text-xl font-semibold">Mobile App</h2>
        <p className="mb-6 text-sm text-slate-400">
          React Native — iOS and Android. Full navigation, biometric auth, live trading.
        </p>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {[
            "Bottom-tab navigation: Watchlist, Trade, Positions, Orders, Portfolio, Account",
            "Biometric authentication (Face ID / fingerprint) + JWT keychain storage",
            "Watchlist screen — live bid/ask, tap symbol to trade",
            "Trade screen — BUY/SELL, MARKET/LIMIT order ticket",
            "Positions screen — open positions with per-row close + flatten-all",
            "Orders screen — order history with status pills",
            "Portfolio screen — equity, cash, unrealized/realized P&L",
            "Account screen — trading account picker + sign-out",
            "Push notifications (APNS / FCM)",
            "AsyncStorage-backed active trading account across sessions",
          ].map((f) => (
            <div key={f} className="flex gap-2 text-sm text-slate-300">
              <span className="mt-0.5 shrink-0 text-slate-700">–</span>
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Footer CTA */}
      <div className="border-t border-slate-800 px-6 py-12 text-center">
        <Link
          to="/login"
          className="inline-block rounded bg-slate-100 px-6 py-2.5 text-sm font-medium text-slate-900 hover:bg-white"
        >
          Get started
        </Link>
        <p className="mt-3 text-xs text-slate-600">
          Demo account available — $100,000 paper funds, no email verification required.
        </p>
      </div>
    </div>
  );
}
